import { useCallback, useEffect, useRef, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isNodeSelection,
  BaseSelection,
  COMMAND_PRIORITY_LOW,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from 'lexical';

import { TrackingLinkDisplay } from '../../../tracking-link-display';

import { $isTrackingNode } from './tracking-link-node';
import { useTrackingLinkComposer } from './TrackingLinkComposer';

export function TrackingLink({
  target,
  title,
  setTitle,
  nodeKey,
}: {
  target: string;
  title?: string;
  setTitle: (title: string) => void;
  nodeKey: string;
}) {
  const triggered = useRef(false);
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const [selection, setSelection] = useState<BaseSelection | null>(null);

  const { insertTrackingLink } = useTrackingLinkComposer();

  const isFocused = $isNodeSelection(selection) && isSelected;

  const $onDelete = useCallback(
    (payload: KeyboardEvent) => {
      const deleteSelection = $getSelection();
      if (isSelected && $isNodeSelection(deleteSelection)) {
        const event: KeyboardEvent = payload;
        event.preventDefault();
        editor.update(() => {
          deleteSelection.getNodes().forEach((node) => {
            if ($isTrackingNode(node)) {
              node.remove();
            }
          });
        });
      }
      return false;
    },
    [editor, isSelected]
  );

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        setSelection(editorState.read(() => $getSelection()));
      }),
      editor.registerCommand<KeyboardEvent>(
        KEY_ARROW_LEFT_COMMAND,
        (event) => {
          const selection = $getSelection();
          if (isSelected && $isNodeSelection(selection)) {
            event.preventDefault();
            editor.update(() => {
              const node = selection.getNodes()[0];
              if ($isTrackingNode(node)) {
                node.selectPrevious();
              }
            });
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand<KeyboardEvent>(
        KEY_ARROW_RIGHT_COMMAND,
        (event) => {
          const selection = $getSelection();
          if (isSelected && $isNodeSelection(selection)) {
            event.preventDefault();
            editor.update(() => {
              const node = selection.getNodes()[0];
              if ($isTrackingNode(node)) {
                node.selectNext();
              }
            });
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(KEY_DELETE_COMMAND, $onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, $onDelete, COMMAND_PRIORITY_LOW)
    );
  }, [clearSelection, editor, isSelected, nodeKey, $onDelete, setSelected]);

  useEffect(() => {
    async function runAsync() {
      if (!title && !triggered.current) {
        triggered.current = true;
        const ret = await insertTrackingLink?.(target, true);
        if (!ret) return;

        editor.update(() => {
          setTitle(ret.shortUrl || ret.trackingUrl);
        });
      }
    }

    runAsync();
  }, [editor, insertTrackingLink, setTitle, target, title]);

  return <TrackingLinkDisplay title={title} target={target} isFocused={isFocused} />;
}
